import type {
  ActionType,
  UsageResetPeriod,
} from '@monorepo/shared/schemas/common'
import {
  ArrowLeft,
  Bot,
  Infinity as InfinityIcon,
  Save,
  X,
  Zap,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import type { Agent } from '@/features/agents/types/agent.types'
import { AgentSelect } from '@/shared/components/common/AgentSelect'
import type { Entitlement } from '@/shared/components/common/EntitlementsTable'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import { Switch } from '@/shared/components/ui/switch'
import { AVAILABLE_FEATURES, USAGE_RESET_OPTIONS } from './constants'

interface EntitlementCreateEditProps {
  availableAgents: Agent[]
  editingEntitlement: Entitlement | null
  onSave: (entitlement: Entitlement) => void
  onCancel: () => void
}

export function EntitlementCreateEdit({
  availableAgents,
  editingEntitlement,
  onSave,
  onCancel,
}: EntitlementCreateEditProps) {
  // State definitions
  const [entitlementType, setEntitlementType] = useState<'features' | 'agents'>(
    'agents'
  )

  // Form state
  const [selectedFeature, setSelectedFeature] = useState('')
  const [selectedAgent, setSelectedAgent] = useState('')
  const [selectedSignal, setSelectedSignal] = useState('')
  const [featureValue, setFeatureValue] = useState<number | 'infinity'>(0)
  const [agentValue, setAgentValue] = useState<number | 'infinity'>(0)
  const [usageReset, setUsageReset] = useState<UsageResetPeriod>('monthly')
  const [isHardLimit, setIsHardLimit] = useState(false)

  // Reset form when switching to create/edit
  useEffect(() => {
    if (editingEntitlement) {
      // Editing existing entitlement
      if (editingEntitlement.type === 'feature') {
        setEntitlementType('features')
        const featureId =
          AVAILABLE_FEATURES.find(f => f.name === editingEntitlement.name)
            ?.id || ''
        setSelectedFeature(featureId)
        setFeatureValue(editingEntitlement.value)
      } else {
        setEntitlementType('agents')
        const agent = availableAgents.find(
          a => a.name === editingEntitlement.agentName
        )
        setSelectedAgent(agent?.id || '')
        const signal = agent?.signals?.find(
          s => s.name === editingEntitlement.signalName
        )
        setSelectedSignal(signal?.id || '')
        setAgentValue(editingEntitlement.value)
      }
      setUsageReset(editingEntitlement.usageReset)
      setIsHardLimit(editingEntitlement.isHardLimit)
    } else {
      // Creating new entitlement
      setSelectedFeature('')
      setSelectedAgent('')
      setSelectedSignal('')
      setFeatureValue(0)
      setAgentValue(0)
      setUsageReset('monthly')
      setIsHardLimit(false)
      setEntitlementType('agents')
    }
  }, [editingEntitlement, availableAgents])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    let entitlement: Entitlement

    if (entitlementType === 'features') {
      const feature = AVAILABLE_FEATURES.find(f => f.id === selectedFeature)
      if (!feature) return

      entitlement = {
        id: editingEntitlement?.id || `ent_${Date.now()}`,
        type: 'feature',
        name: feature.name,
        description: feature.description,
        value: currentValue,
        usageReset,
        isHardLimit,
      }
    } else {
      const agent = availableAgents.find(a => a.id === selectedAgent)
      const signal = agent?.signals?.find(s => s.id === selectedSignal)
      if (!agent || !signal) return

      entitlement = {
        id: editingEntitlement?.id || `ent_${Date.now()}`,
        type: 'agent',
        name: `${agent.name} - ${signal.name}`,
        agentName: agent.name,
        signalName: signal.name,
        signalKind: signal.kind as ActionType,
        value: currentValue,
        usageReset,
        isHardLimit,
      }
    }

    onSave(entitlement)
  }

  const toggleInfinity = () => {
    if (entitlementType === 'features') {
      setFeatureValue(current => (current === 'infinity' ? 0 : 'infinity'))
    } else {
      setAgentValue(current => (current === 'infinity' ? 0 : 'infinity'))
    }
  }

  const selectedAgentData = availableAgents.find(a => a.id === selectedAgent)
  const currentValue =
    entitlementType === 'features' ? featureValue : agentValue
  const isInfinity = currentValue === 'infinity'

  // Check if form is valid
  const isFormValid =
    entitlementType === 'features'
      ? selectedFeature && (isInfinity || currentValue > 0)
      : selectedAgent && selectedSignal && (isInfinity || currentValue > 0)

  return (
    <div className='space-y-6'>
      {/* Header */}
      <Card className='bg-transparent'>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                <ArrowLeft
                  className='w-5 h-5 cursor-pointer'
                  onClick={onCancel}
                />
                {editingEntitlement ? 'Edit' : 'Add'} Entitlement
              </CardTitle>
              <p className='text-sm text-muted-foreground'>
                Configure access limits for agent signals and features
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className='space-y-6'>
            {/* Entitlement Type Selection */}
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <Label className='text-base font-medium'>
                  Entitlement Type
                </Label>
                <div className='flex items-center gap-3'>
                  <div className='flex items-center gap-2'>
                    <Bot className='w-4 h-4 text-muted-foreground' />
                    <span className='text-sm'>Agents</span>
                  </div>
                  <Switch
                    checked={entitlementType === 'features'}
                    onCheckedChange={checked =>
                      setEntitlementType(checked ? 'features' : 'agents')
                    }
                  />
                  <div className='flex items-center gap-2'>
                    <Zap className='w-4 h-4 text-muted-foreground' />
                    <span className='text-sm'>Features</span>
                  </div>
                </div>
              </div>

              {/* Form Fields Based on Type */}
              {entitlementType === 'agents' ? (
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label>Select Agent</Label>
                    <AgentSelect
                      agents={availableAgents}
                      value={selectedAgent}
                      onValueChange={value => {
                        setSelectedAgent(value)
                        setSelectedSignal('')
                      }}
                      placeholder='Choose an agent'
                      className='mt-3'
                    />
                  </div>

                  <div>
                    <Label>Select Signal</Label>
                    <Select
                      value={selectedSignal}
                      onValueChange={setSelectedSignal}
                      disabled={!selectedAgent}
                    >
                      <SelectTrigger className='mt-3'>
                        <SelectValue placeholder='Choose a signal to limit' />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedAgentData?.signals?.map(signal => (
                          <SelectItem key={signal.id} value={signal.id}>
                            {signal.name} ({signal.kind})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label>Select Feature</Label>
                    <Select
                      value={selectedFeature}
                      onValueChange={setSelectedFeature}
                    >
                      <SelectTrigger className='mt-3'>
                        <SelectValue placeholder='Choose a feature to limit' />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_FEATURES.map(feature => (
                          <SelectItem key={feature.id} value={feature.id}>
                            {feature.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Configuration */}
            <div className='space-y-4'>
              <Label className='text-base font-medium'>Configuration</Label>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <Label>Usage Limit</Label>
                  <div className='flex gap-2 mt-3'>
                    <Input
                      type='number'
                      value={isInfinity ? '' : (currentValue as number)}
                      onChange={e => {
                        const newValue = e.target.value
                          ? Number(e.target.value)
                          : 0
                        if (entitlementType === 'features') {
                          setFeatureValue(newValue)
                        } else {
                          setAgentValue(newValue)
                        }
                      }}
                      placeholder={isInfinity ? 'Unlimited' : '0'}
                      disabled={isInfinity}
                      className='flex-1'
                    />
                    <Button
                      type='button'
                      variant={isInfinity ? 'default' : 'outline'}
                      onClick={toggleInfinity}
                      className='px-3'
                    >
                      <InfinityIcon className='w-4 h-4' />
                    </Button>
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    Set the maximum usage allowed per billing period
                  </p>
                </div>

                <div>
                  <Label>Usage Reset Period</Label>
                  <Select
                    value={usageReset}
                    onValueChange={value =>
                      setUsageReset(value as typeof usageReset)
                    }
                  >
                    <SelectTrigger className='mt-3'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {USAGE_RESET_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className='text-xs text-muted-foreground mt-1'>
                    How often usage counters reset
                  </p>
                </div>
              </div>

              <div className='flex items-center space-x-2'>
                <Switch
                  checked={isHardLimit}
                  onCheckedChange={setIsHardLimit}
                />
                <div className='space-y-1'>
                  <Label className='text-sm'>Hard Limit</Label>
                  <p className='text-xs text-muted-foreground'>
                    {isHardLimit
                      ? 'Access will be blocked when limit is reached'
                      : 'Access continues with overage charges after limit'}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className='flex justify-end gap-3 pt-4 border-t'>
              <Button type='button' variant='outline' onClick={onCancel}>
                <X className='w-4 h-4 mr-2' />
                Cancel
              </Button>
              <Button type='submit' disabled={!isFormValid}>
                <Save className='w-4 h-4 mr-2' />
                {editingEntitlement ? 'Update' : 'Add'} Entitlement
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
